import { createSlice } from "@reduxjs/toolkit";
import {
  fetchMeetings,
  fetchMeetingsByDate,
  fetchMeetingsForCurrentWeek,
  checkMeetingConflicts,
  createMeeting,
  updateMeetingThunk,
  deleteMeetingThunk,
  fetchMeetingById,
  fetchRecurringMeetingSeries,
  previewRecurringMeetings,
  updateRecurringMeetingSeries,
  deleteRecurringMeetingSeries,
} from "../thunks/meetingsThunk";

const initialState = {
  meetings: [],
  loading: false,
  error: null,
  selectedMeeting: null,
  conflicts: null,  // Holds conflict data when available
  meetingUpdated: false,
  // Recurring meeting state
  recurringSeries: [],
  recurringPreview: null,
  recurringPreviewLoading: false,
};

const meetingSlice = createSlice({
  name: "meetings",
  initialState,
  reducers: {
    setSelectedMeeting: (state, action) => {
      state.selectedMeeting = action.payload;
    },
    resetError: (state) => {
      state.error = null;
    },
    resetConflicts: (state) => {
      state.conflicts = null;
    },
    setMeetingUpdated: (state, action) => {
      state.meetingUpdated = action.payload;
    },
    resetMeetingUpdated: (state) => {
      state.meetingUpdated = false;
    },
    resetRecurringPreview: (state) => {
      state.recurringPreview = null;
      state.recurringPreviewLoading = false;
    },
    clearRecurringSeries: (state) => {
      state.recurringSeries = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Meetings
      .addCase(fetchMeetings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMeetings.fulfilled, (state, action) => {
        state.loading = false;
        state.meetings = action.payload;
      })
      .addCase(fetchMeetings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch meetings.";
      })

      // Fetch Meetings By Date
      .addCase(fetchMeetingsByDate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMeetingsByDate.fulfilled, (state, action) => {
        state.loading = false;
        state.meetings = action.payload;
      })
      .addCase(fetchMeetingsByDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Meetings For Current Week
      .addCase(fetchMeetingsForCurrentWeek.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMeetingsForCurrentWeek.fulfilled, (state, action) => {
        state.loading = false;
        state.meetings = action.payload;
      })
      .addCase(fetchMeetingsForCurrentWeek.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Check Meeting Conflicts
      .addCase(checkMeetingConflicts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkMeetingConflicts.fulfilled, (state, action) => {
        state.loading = false;
        state.conflicts = action.payload;  // Store conflict data
      })
      .addCase(checkMeetingConflicts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Meeting
      .addCase(createMeeting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMeeting.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both old single meeting format and new recurring format
        const meeting = action.payload.meeting || action.payload;
        state.meetings.push(meeting);
        state.meetingUpdated = true;
      })
      .addCase(createMeeting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Meeting
      .addCase(updateMeetingThunk.fulfilled, (state, action) => {
        const index = state.meetings.findIndex(
          (meeting) => meeting._id === action.payload._id
        );
        if (index !== -1) {
          state.meetings[index] = action.payload;
          state.meetingUpdated = true;
        }
      })

      // Delete Meeting
      .addCase(deleteMeetingThunk.fulfilled, (state, action) => {
        state.meetings = state.meetings.filter(
          (meeting) => meeting._id !== action.payload
        );
        state.meetingUpdated = true;
      })

      // Fetch Meeting By ID
      .addCase(fetchMeetingById.fulfilled, (state, action) => {
        state.selectedMeeting = action.payload;
      })

      // Fetch Recurring Meeting Series
      .addCase(fetchRecurringMeetingSeries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecurringMeetingSeries.fulfilled, (state, action) => {
        state.loading = false;
        state.recurringSeries = action.payload.meetings || [];
      })
      .addCase(fetchRecurringMeetingSeries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Preview Recurring Meetings
      .addCase(previewRecurringMeetings.pending, (state) => {
        state.recurringPreviewLoading = true;
        state.error = null;
      })
      .addCase(previewRecurringMeetings.fulfilled, (state, action) => {
        state.recurringPreviewLoading = false;
        state.recurringPreview = action.payload;
      })
      .addCase(previewRecurringMeetings.rejected, (state, action) => {
        state.recurringPreviewLoading = false;
        state.error = action.payload;
      })

      // Update Recurring Meeting Series
      .addCase(updateRecurringMeetingSeries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRecurringMeetingSeries.fulfilled, (state, action) => {
        state.loading = false;
        // Update meetings in the current list that were part of the series
        const updatedMeetings = action.payload.meetings || [];
        updatedMeetings.forEach((updatedMeeting) => {
          const index = state.meetings.findIndex(
            (meeting) => meeting._id === updatedMeeting._id
          );
          if (index !== -1) {
            state.meetings[index] = updatedMeeting;
          }
        });
        state.meetingUpdated = true;
      })
      .addCase(updateRecurringMeetingSeries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Recurring Meeting Series
      .addCase(deleteRecurringMeetingSeries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRecurringMeetingSeries.fulfilled, (state, action) => {
        state.loading = false;
        // Remove all meetings that belonged to this recurring group
        const { recurringGroupId } = action.payload;
        state.meetings = state.meetings.filter(
          (meeting) => meeting.recurringGroup !== recurringGroupId
        );
        state.meetingUpdated = true;
      })
      .addCase(deleteRecurringMeetingSeries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedMeeting,
  resetError,
  resetConflicts,
  setMeetingUpdated,
  resetMeetingUpdated,
  resetRecurringPreview,
  clearRecurringSeries
} = meetingSlice.actions;
export default meetingSlice.reducer;
